import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { sumBy } from 'lodash';
import { Col, Form, Row } from 'antd';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import {
  getFinContractViewByTpidandDate,
  FinContractViewResponse,
} from 'services/fincontract';
import { toCurrency } from 'utils/utils';
import Value from 'components/Value';
import FormInput from 'components/FormInput';
import ModalControllerNV from 'components/ModalControllerNV';
import ProposalModal from 'pages/ProposalV3/ProposalModal';

export type TPContractsProps = {
  tpid?: number;
  showAsModal?: boolean;
  currcode?: string;
};
const TPContracts = ({ tpid, showAsModal, currcode }: TPContractsProps) => {
  const [form] = Form.useForm();

  const [state, setState] = useState<any>({
    isLoading: false,
    dataCalc: [],
    totalFinanced: 0,
    totaloutstandingamount: 0,
    totalunpaidamount: 0,
    totalSelected: 0,
    listCalid: [],
    totalrvamount: '',
  });

  const { data, isFetching } = useQuery(
    ['getFinContractViewByTpidandDate', tpid],
    () =>
      getFinContractViewByTpidandDate({
        tpid: !isNaN(tpid!) ? tpid! : 0,
        evalDate: '',
      }).then((res) => {
        return res.list || [];
      }),
    { staleTime: 90 * 10000 }
  );

  if (data) {
    state.totalFinanced = sumBy(data, 'ctfinancedamount');
    state.totalunpaidamount = sumBy(data, 'totalunpaidamount');
    state.totaloutstandingamount = sumBy(data, 'totaloutstandingamount');
    state.totalrvamount = sumBy(data, 'rvamount');
    state.currcode = data[0]?.elements[0]?.currcode;
  } else {
    state.totalFinanced = 0;
    state.totalunpaidamount = 0;
    state.totaloutstandingamount = 0;
    state.totalrvamount = 0;
  }
  // <ContractDetailModal
  //   ctreference={ctreference}
  //   ctrid={record.ctrid}
  //   showAsModal
  // />

  const columns = useMemo<DataGridColDef<FinContractViewResponse>[]>(
    () => [
      {
        dataIndex: 'ctreference',
        width: '150px',
        render: (ctreference, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{ctreference}</a>
            )}
            modal={({ setVisible, visible }) => (
              <ProposalModal
                ctrid={record.ctrid}
                visible={visible}
                onCancel={() => setVisible(false)}
                onSuccess={() => setVisible(false)}
                title=""
                iscreate={false}
                phase="PROD"
              />
            )}
          ></ModalControllerNV>
        ),
        fixed:'left',
      },
      {
        dataIndex: 'ctdescription',

        width: '250px',
      },

      {
        dataIndex: 'offerlabel',
        width: '150px',
      },
      {
        dataIndex: 'prcode',
        width: '100px',
      },
      {
        dataIndex: 'statuslabel',
        width: '100px',
      },
      {
        dataIndex: 'ctactivationdate',
        width: '100px',
      },
      // Commented the field because it returns multiple elements: case not yet treated
      // {
      //   dataIndex: 'ctenddate',
      //   width: '100px',
      // },
      {
        dataIndex: 'ctduration',
        width: '100px',
      },
      // Commented the field because it returns multiple elements: case not yet treated
      // {
      //   dataIndex: 'ctdurationunit',
      //   width: '100px',
      // },
      {
        dataIndex: 'ctremainingduration',
        width: '100px',
      },
      {
        dataIndex: 'evaluationdate',
        width: '100px',
      },

      {
        dataIndex: 'nextinvoicenetamount',
        width: '100px',
        render: (val, record) =>
          toCurrency(
            val,
            record?.assets[0]?.currcode ?? record?.elements[0]?.currcode
          ),
      },
      {
        dataIndex: 'nextinvoicedate',
        width: '100px',
      },
      // Commented the field because it returns multiple elements: case not yet treated
      // {
      //   dataIndex: 'outstandingdate',
      //   width: '100px',
      // },
      {
        dataIndex: 'ctfinancedamount',
        width: '100px',
        render: (val, record) =>
          toCurrency(
            val,
            record?.assets[0]?.currcode ?? record?.elements[0]?.currcode
          ),

        fixed:'right',
      },

      {
        dataIndex: 'totalunpaidamount',
        width: '100px',
        render: (val, record) =>
          toCurrency(
            val,
            record?.assets[0]?.currcode ?? record?.elements[0]?.currcode
          ),

        fixed: 'right',
      },

      {
        dataIndex: 'totaloutstandingamount',
        width: '100px',
        render: (val, record) =>
          toCurrency(
            val,
            record?.assets[0]?.currcode ?? record?.elements[0]?.currcode
          ),

        fixed: 'right',
      },
    ],

    []
  );

  // eslint-disable-next-line no-unused-vars
  const updateState = useCallback(
    (values: any) => {
      setState((prev: any) => ({ ...prev, ...values }));
    },
    [setState]
  );

  return (
    <DF.Provider pagcode="TPContracts">
      <Form form={form} name="form">
        <Row>
          <Col span={6}>
            <Form.Item name="totalFinanced" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totalFinanced">
                <Value
                  value={toCurrency(
                    state.totalFinanced,
                    state.currcode ?? localStorage.getItem('currcode') ?? ''
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="totaloutstandingamount" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totaloutstandingamount">
                <Value
                  value={toCurrency(
                    state.totaloutstandingamount,
                    state.currcode ?? localStorage.getItem('currcode') ?? ''
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="totalrvamount" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totalrvamount">
                <Value
                  value={toCurrency(
                    state.totalrvamount,
                    state.currcode ?? localStorage.getItem('currcode') ?? ''
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="totalunpaidamount" shouldUpdate noStyle>
              <FormInput col xs={24} comcode="totalunpaidamount">
                <Value
                  value={toCurrency(
                    state.totalunpaidamount,
                    state.currcode ?? localStorage.getItem('currcode') ?? ''
                  )}
                />
              </FormInput>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <DataGrid
        dataSource={data ?? []}
        titleComcode="_contact"
        columns={columns}
        isLoading={isFetching}
        rowKey={(record) => record.ctrid}
        scroll={{ x: 1000, y: 400 }}
        pagination={{ pageSize: 1000 }}
       
      ></DataGrid>
    </DF.Provider>
  );
};

export default TPContracts;
